import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { fetchAuctionTeamSetup, fetchTournaments } from '../../../services/tournamentService';
import { getAuctionManagerPath } from '../../../utils/organization';
import {
  getAuctionManagerPath as getCommunityAuctionManagerPath,
  getCommunityId,
} from '../../../utils/community';
import { getAuctionManagerPath as getClubAuctionManagerPath, getClubId } from '../../../utils/club';
import PageShell from '../../../components/layout/PageShell';

const emptySetup = { playersPerTeam: 0, teams: [] };

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const AuctionTeamsOverviewPage = () => {
  const { token } = useAuth();
  const { auctionManagerId, organizationId, communityId: routeCommunityId, clubId: routeClubId, tournamentId } = useParams();
  const [searchParams] = useSearchParams();
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState('');
  const [setup, setSetup] = useState(emptySetup);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const communityId = getCommunityId(routeCommunityId);
  const clubId = getClubId(routeClubId);
  const auctionManagementPath =
    (communityId
      ? getCommunityAuctionManagerPath(communityId, auctionManagerId, tournamentId)
      : clubId
        ? getClubAuctionManagerPath(clubId, auctionManagerId, tournamentId)
        : getAuctionManagerPath(organizationId, auctionManagerId, tournamentId)) || '/management/auction';
  const teamSetupPath = selectedTournamentId
    ? `${auctionManagementPath}/teams?tournamentId=${selectedTournamentId}`
    : `${auctionManagementPath}/teams`;

  useEffect(() => {
    const urlTournamentId = searchParams.get('tournamentId');
    if (urlTournamentId) {
      setSelectedTournamentId(urlTournamentId);
      return;
    }

    if (tournamentId) {
      setSelectedTournamentId(tournamentId);
    }
  }, [searchParams, tournamentId]);

  useEffect(() => {
    if (!token) return;

    const loadTournaments = async () => {
      try {
        const response = await fetchTournaments(token, organizationId, { communityId, clubId });
        const availableTournaments = (response.tournaments || [])
          .map((tournament) => ({ id: tournament.id || tournament._id, name: tournament.name }))
          .filter((tournament) => Boolean(tournament.id));
        setTournaments(availableTournaments);

        if (!selectedTournamentId && availableTournaments.length) {
          setSelectedTournamentId(availableTournaments[0].id);
        }
      } catch (loadError) {
        setError(loadError.message || 'Unable to load tournaments.');
      }
    };

    loadTournaments();
  }, [clubId, communityId, organizationId, selectedTournamentId, token]);

  useEffect(() => {
    if (!selectedTournamentId || !token) return;

    const loadSetup = async () => {
      setIsLoading(true);
      setError('');
      try {
        const response = await fetchAuctionTeamSetup(selectedTournamentId, token);
        setSetup(response.setup || emptySetup);
      } catch (loadError) {
        setSetup(emptySetup);
        setError(loadError.message || 'Unable to load saved teams for this tournament.');
      } finally {
        setIsLoading(false);
      }
    };

    loadSetup();
  }, [selectedTournamentId, token]);

  const renderPlayerList = (team) => {
    if (!team.players?.length) {
      return <p className="text-muted mb-0">No players assigned yet.</p>;
    }

    return (
      <ul className="list-unstyled mb-0 d-grid gap-2">
        {team.players.map((player) => {
          const name = player.name || player.fullName || player.email || 'Unnamed player';
          const role = player.role || player.primaryRole;
          const email = player.email;
          const image = player.image || player.profilePhoto || player.media?.profilePhoto;
          const initial = name?.charAt(0)?.toUpperCase?.() || '?';

          return (
            <li key={player.id || name} className="d-flex align-items-center gap-3">
              <div className="flex-shrink-0">
                {image ? (
                  <img
                    src={image}
                    alt={name}
                    className="rounded-circle"
                    style={{ width: '44px', height: '44px', objectFit: 'cover' }}
                  />
                ) : (
                  <div
                    className="rounded-circle bg-secondary-subtle text-secondary-emphasis d-flex align-items-center justify-content-center"
                    style={{ width: '44px', height: '44px' }}
                    aria-hidden="true"
                  >
                    <span className="fw-semibold">{initial}</span>
                  </div>
                )}
              </div>
              <div className="flex-grow-1">
                <p className="fw-semibold mb-0">{name}</p>
                {email && <p className="text-muted small mb-0">{email}</p>}
                {role && <span className="badge bg-secondary-subtle text-secondary-emphasis mt-1">{role}</span>}
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="py-4 d-grid gap-4">
      <div className="card border-0 shadow-sm overflow-hidden">
        <div className="card-body d-flex flex-column flex-lg-row justify-content-between align-items-start gap-3">
          <div className="d-flex flex-column gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <p className="eyebrow text-uppercase text-primary fw-semibold mb-0">Auction manager tools</p>
              <span className="badge bg-success-subtle text-success-emphasis">Teams overview</span>
            </div>
            <h1 className="h4 mb-1">Saved auction teams</h1>
            <p className="text-muted mb-0">
              Review the teams captured for your tournament with their logos, rosters, and leadership roles.
            </p>
          </div>
          <div className="d-flex flex-column flex-lg-row gap-2 align-self-stretch">
            <Link to={teamSetupPath} className="btn btn-outline-primary w-100">
              Back to team setup
            </Link>
          </div>
        </div>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-body d-grid gap-3">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-lg-6">
              <label htmlFor="tournament-select" className="form-label fw-semibold">
                Tournament
              </label>
              <select
                id="tournament-select"
                className="form-select"
                value={selectedTournamentId}
                onChange={(event) => setSelectedTournamentId(event.target.value)}
                disabled={!token || !tournaments.length}
              >
                <option value="" disabled>
                  {token ? 'Select a tournament to view teams' : 'Sign in to view tournaments'}
                </option>
                {tournaments.map((tournament) => (
                  <option key={tournament.id} value={tournament.id}>
                    {tournament.name}
                  </option>
                ))}
              </select>
              <small className="text-muted">Choose the tournament whose saved teams you want to review.</small>
            </div>
            <div className="col-12 col-lg-6">
              <div className="border rounded p-3 bg-light h-100 d-flex flex-column justify-content-center gap-2">
                <div className="d-flex align-items-center gap-2 z-1">
                  <span className="badge bg-primary-subtle text-primary-emphasis">{setup.playersPerTeam || '—'}</span>
                  <div>
                    <p className="fw-semibold mb-0">Players per team</p>
                    <p className="text-muted small mb-0">Saved roster limit applied to each squad.</p>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-success-subtle text-success-emphasis">{setup.teams?.length || 0}</span>
                  <div>
                    <p className="fw-semibold mb-0">Team count</p>
                    <p className="text-muted small mb-0">Active squads saved for this tournament.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {error && <div className="alert alert-danger mb-0">{error}</div>}

          {isLoading ? (
            <p className="text-muted mb-0">Loading saved teams...</p>
          ) : setup.teams?.length ? (
            <div className="row g-3">
              {setup.teams.map((team, index) => {
                const captain = team.players?.find((player) => (player.id || player._id) === team.captain);
                const viceCaptain = team.players?.find((player) => (player.id || player._id) === team.viceCaptain);
                const wicketKeeper = team.players?.find((player) => (player.id || player._id) === team.wicketKeeper);
                const initial = team.name?.charAt(0)?.toUpperCase?.() || 'T';

                return (
    <PageShell containerClassName="p-0">
                  <div className="col-12 col-md-6 col-xl-4" key={team.name + index}>
                    <div className="card h-100 border-0 shadow-sm">
                      <div className="card-body d-grid gap-3">
                        <div className="d-flex align-items-center gap-3">
                          {team.logoUrl ? (
                            <img
                              src={team.logoUrl}
                              alt={team.name}
                              className="rounded"
                              style={{ width: '64px', height: '64px', objectFit: 'cover' }}
                            />
                          ) : (
                            <div
                              className="rounded bg-secondary-subtle text-secondary-emphasis d-flex align-items-center justify-content-center"
                              style={{ width: '64px', height: '64px' }}
                              aria-hidden="true"
                            >
                              <span className="fw-bold">{initial}</span>
                            </div>
                          )}
                          <div>
                            <p className="fw-semibold mb-0">{team.name || `Team ${index + 1}`}</p>
                            <p className="text-muted mb-0 small">
                              {team.players?.length || 0} / {setup.playersPerTeam || '—'} players listed
                            </p>
                          </div>
                        </div>

                        <div className="d-grid gap-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">Captain</span>
                            <span className="fw-semibold">{captain?.name || 'Not selected'}</span>
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">Vice captain</span>
                            <span className="fw-semibold">{viceCaptain?.name || 'Not selected'}</span>
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">Wicket keeper</span>
                            <span className="fw-semibold">{wicketKeeper?.name || 'Not selected'}</span>
                          </div>
                        </div>

                        <div className="d-grid gap-2">
                          <p className="fw-semibold mb-1">Players</p>
                          <div className="border rounded p-2 bg-light" style={{ maxHeight: '240px', overflowY: 'auto' }}>
                            {renderPlayerList(team)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                
    </PageShell>
  );
              })}
            </div>
          ) : (
            <div className="alert alert-info mb-0" role="status">
              <div className="d-flex flex-column gap-1">
                <p className="fw-semibold mb-0">No teams saved yet.</p>
                <p className="text-muted mb-0">
                  Save your team setup first, then return here to view all squads with their players and leadership roles.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuctionTeamsOverviewPage;
